import { getAllTasks, getTaskById } from '@/server/tasks';
import { validator } from '@/server/validators/index';
import type { Task as ServerTask } from '@/server/types';
import type { ValidationResponse } from '@/server/types';

/** Задание в UI = данные с сервера + флаги клиента */
export type Task = ServerTask & { isCompleted?: boolean };

export interface ValidationError {
    path: string;
    expected: string;
    actual: any;
    message: string;
}

export interface ValidationResult {
    success: boolean;
    taskId: string;
    errors?: ValidationError[];
    message?: string;
    hint?: string;
}

export interface TaskResponse {
    success: boolean;
    task?: Task;
    validationResult?: ValidationResult;
    error?: string;
}

function mapValidationResult(r: ValidationResponse): ValidationResult {
    return {
        success: r.success,
        taskId: r.taskId,
        errors: r.errors as ValidationError[] | undefined,
        message: r.message,
        hint: r.hint,
    };
}

// Локальная логика: задачи и валидация в бандле (см. bun run build)
export class TaskService {
    async getTaskById(taskId: string): Promise<TaskResponse> {
        try {
            const task = getTaskById(taskId);
            if (!task) {
                return { success: false, error: `Task ${taskId} not found` };
            }
            return { success: true, task: task as Task };
        } catch (error) {
            console.error('Error fetching task:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async getTaskByStep(step: number): Promise<TaskResponse> {
        return this.getTaskById(step.toString());
    }

    async getAllTasks(): Promise<Task[]> {
        try {
            return getAllTasks() as Task[];
        } catch (error) {
            console.error('Error fetching all tasks:', error);
            return [];
        }
    }

    async submitTaskSolution(taskId: string, yaml: string): Promise<TaskResponse> {
        try {
            const task = getTaskById(taskId);
            if (!task) {
                return {
                    success: false,
                    error: `Задание с ID ${taskId} не найдено`,
                };
            }

            const result = await validator.validateManifest({ taskId, yaml });
            return {
                success: result.success,
                validationResult: mapValidationResult(result),
                error: result.success ? undefined : (result.message || 'Validation failed'),
            };
        } catch (error) {
            console.error('Error submitting task solution:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }
}

export const taskService = new TaskService();
